// The kind of ending (`GameFacts.endingType`), classified the way endgame
// books do: by the material each side has, as the chapters of Müller &
// Lamprecht's "Fundamental Chess Endings" are organised (knight endings,
// bishop endings of the same and of opposite colours, bishop against knight,
// rook endings, rook against minor piece, queen against rook, …). One
// position's class reads each side's pieces besides king and pawns; pawns do
// not change it (`endingTypeOf`). An ending's class is the material it was
// played with (`endingTypeOfPositions`): the piece signature that lasts the
// most plies from the ending's start to the game's last position, since the
// ending often starts while pieces are still coming off. The `EndingType`
// union itself is the model's (`@chess/api-model`), since the facts and the
// library filter carry it.
import type { EndingType } from '@chess/api-model';

/** Every kind of ending, in the books' order: pawns, minor pieces, rooks, queens, then the imbalances. */
export const ENDING_TYPES = [
  'pawn',
  'knight',
  'bishop-same',
  'bishop-opposite',
  'bishop-knight',
  'minor',
  'rook',
  'rook-minor',
  'rook-vs-minor',
  'double-rook',
  'queen',
  'queen-vs-rook',
  'piece-vs-pawns',
  'mixed',
] as const satisfies readonly EndingType[];

export const ENDING_TYPE_LABEL: Record<EndingType, string> = {
  pawn: 'Pawn ending',
  knight: 'Knight ending',
  'bishop-same': 'Bishop ending, same colour',
  'bishop-opposite': 'Opposite-coloured bishops',
  'bishop-knight': 'Bishop vs knight',
  minor: 'Minor-piece ending',
  rook: 'Rook ending',
  'rook-minor': 'Rook and minor-piece ending',
  'rook-vs-minor': 'Rook vs minor piece',
  'double-rook': 'Double-rook ending',
  queen: 'Queen ending',
  'queen-vs-rook': 'Queen vs rook',
  'piece-vs-pawns': 'Pieces vs pawns',
  mixed: 'Mixed-piece ending',
};

/** The heaviest piece family of each kind, for grouping in compact charts. */
export type EndingFamily = 'pawn' | 'minor' | 'rook' | 'queen' | 'other';
export const ENDING_FAMILY: Record<EndingType, EndingFamily> = {
  pawn: 'pawn',
  knight: 'minor',
  'bishop-same': 'minor',
  'bishop-opposite': 'minor',
  'bishop-knight': 'minor',
  minor: 'minor',
  rook: 'rook',
  'rook-minor': 'rook',
  'rook-vs-minor': 'rook',
  'double-rook': 'rook',
  queen: 'queen',
  'queen-vs-rook': 'queen',
  'piece-vs-pawns': 'other',
  mixed: 'other',
};

/** One side's pieces besides king and pawns; `light` counts its bishops on light squares. */
interface Side {
  q: number;
  r: number;
  b: number;
  n: number;
  light: number;
}

const empty = (): Side => ({ q: 0, r: 0, b: 0, n: 0, light: 0 });
const pieces = (s: Side) => s.q + s.r + s.b + s.n;
const is = (s: Side, q: number, r: number, b: number, n: number) => s.q === q && s.r === r && s.b === b && s.n === n;

/** Each side's material from a FEN's piece placement. */
function material(fen: string): { white: Side; black: Side } {
  const white = empty();
  const black = empty();
  const ranks = fen.split(' ')[0].split('/');
  ranks.forEach((row, i) => {
    const rank = 7 - i; // the first row is the eighth rank
    let file = 0;
    for (const ch of row) {
      if (ch >= '1' && ch <= '8') {
        file += Number(ch);
        continue;
      }
      const side = ch === ch.toUpperCase() ? white : black;
      const p = ch.toLowerCase();
      if (p === 'q' || p === 'r' || p === 'n') side[p] += 1;
      else if (p === 'b') {
        side.b += 1;
        // a1 (file 0, rank 0) is dark: an odd sum is a light square.
        if ((file + rank) % 2 === 1) side.light += 1;
      }
      file += 1;
    }
  });
  return { white, black };
}

/** The kind of ending of a position, from its FEN's piece placement. */
export function endingTypeOf(fen: string): EndingType {
  const { white: w, black: b } = material(fen);
  const pw = pieces(w);
  const pb = pieces(b);
  if (!pw && !pb) return 'pawn';
  // Minor pieces only, on both sides together.
  if (!w.q && !w.r && !b.q && !b.r) {
    if (is(w, 0, 0, 0, 1) && is(b, 0, 0, 0, 1)) return 'knight';
    if (is(w, 0, 0, 1, 0) && is(b, 0, 0, 1, 0)) return w.light === b.light ? 'bishop-same' : 'bishop-opposite';
    if ((is(w, 0, 0, 1, 0) && is(b, 0, 0, 0, 1)) || (is(w, 0, 0, 0, 1) && is(b, 0, 0, 1, 0))) return 'bishop-knight';
    return 'minor';
  }
  if (!pw || !pb) return 'piece-vs-pawns';
  const pair = (x: (s: Side) => boolean, y: (s: Side) => boolean) => (x(w) && y(b)) || (y(w) && x(b));
  const rookOnly = (s: Side) => is(s, 0, 1, 0, 0);
  const oneMinor = (s: Side) => !s.q && !s.r && s.b + s.n === 1;
  if (rookOnly(w) && rookOnly(b)) return 'rook';
  const rookMinor = (s: Side) => !s.q && s.r === 1 && s.b + s.n === 1;
  if (rookMinor(w) && rookMinor(b)) return 'rook-minor';
  if (pair(rookOnly, oneMinor)) return 'rook-vs-minor';
  if (is(w, 0, 2, 0, 0) && is(b, 0, 2, 0, 0)) return 'double-rook';
  const queenOnly = (s: Side) => is(s, 1, 0, 0, 0);
  if (queenOnly(w) && queenOnly(b)) return 'queen';
  if (pair(queenOnly, rookOnly)) return 'queen-vs-rook';
  return 'mixed';
}

/** A position's piece signature: each side's pieces besides king and pawns; bishops' colours only for B vs B. */
function signatureOf(fen: string): string {
  const { white: w, black: b } = material(fen);
  const side = (s: Side) => `${s.q}${s.r}${s.b}${s.n}`;
  const type = endingTypeOf(fen);
  const colours = type === 'bishop-same' || type === 'bishop-opposite' ? `:${type}` : '';
  return `${side(w)}/${side(b)}${colours}`;
}

/**
 * The kind of an ending from its positions, from its start to the game's last
 * position: the class of the piece signature that lasts the most plies, as
 * endgame books name an ending by the material it was played with. A tie goes
 * to the later signature, which the ending simplified towards. Undefined for
 * no positions.
 */
export function endingTypeOfPositions(fens: readonly string[]): EndingType | undefined {
  const plies = new Map<string, { count: number; last: number; fen: string }>();
  fens.forEach((fen, i) => {
    const key = signatureOf(fen);
    const seen = plies.get(key);
    if (seen) {
      seen.count += 1;
      seen.last = i;
    } else plies.set(key, { count: 1, last: i, fen });
  });
  let best: { count: number; last: number; fen: string } | undefined;
  for (const s of plies.values()) if (!best || s.count > best.count || (s.count === best.count && s.last > best.last)) best = s;
  return best && endingTypeOf(best.fen);
}
